import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import OfflineBanner from "./components/OfflineBanner";
import ConsentBanner from "./components/ConsentBanner";
import HomeButton from "./components/HomeButton";
import "@/App.css";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
const Toaster = lazy(() => import("sonner").then((m) => ({ default: m.Toaster })));

const AppLayout = lazy(() => import("./components/AppLayout"));
const Login = lazy(() => import("./pages/Login"));
const ShopCategory = lazy(() => import("./pages/ShopCategory"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Firma = lazy(() => import("./pages/Firma"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Flotta = lazy(() => import("./pages/Flotta"));
const VeicoloDettaglio = lazy(() => import("./pages/VeicoloDettaglio"));
const Prenotazioni = lazy(() => import("./pages/Prenotazioni"));
const NuovoContratto = lazy(() => import("./pages/NuovoContratto"));
const ContrattoDettaglio = lazy(() => import("./pages/ContrattoDettaglio"));
const Archivio = lazy(() => import("./pages/Archivio"));
const Multe = lazy(() => import("./pages/Multe"));
const MultaDettaglio = lazy(() => import("./pages/MultaDettaglio"));
const CercaMulta = lazy(() => import("./pages/CercaMulta"));
const Tour = lazy(() => import("./pages/Tour"));
const TourDettaglio = lazy(() => import("./pages/TourDettaglio"));
const Barca = lazy(() => import("./pages/Barca"));
const Clienti = lazy(() => import("./pages/Clienti"));
const ClienteDettaglio = lazy(() => import("./pages/ClienteDettaglio"));
const Consegne = lazy(() => import("./pages/Consegne"));
const Report = lazy(() => import("./pages/Report"));
const Impostazioni = lazy(() => import("./pages/Impostazioni"));
const PaymentResult = lazy(() => import("./pages/PaymentResult"));
const ManageBooking = lazy(() => import("./pages/ManageBooking"));
const Partners = lazy(() => import("./pages/Partners"));
const QrFree = lazy(() => import("./pages/QrFree"));
const SiteMedia = lazy(() => import("./pages/SiteMedia"));
const Vintage = lazy(() => import("./pages/Vintage"));
const PartnerDetail = lazy(() => import("./pages/PartnerDetail"));
const PartnerBookings = lazy(() => import("./pages/PartnerBookings"));
const PublicPartnerPage = lazy(() => import("./pages/PublicPartnerPage"));
const QrRedirect = lazy(() => import("./pages/QrRedirect"));
const Portale = lazy(() => import("./pages/Portale"));
const PublicGuide = lazy(() => import("./pages/PublicGuide"));
const GuideAdmin = lazy(() => import("./pages/GuideAdmin"));
const Magazine = lazy(() => import("./pages/Magazine"));
const MagazineAdmin = lazy(() => import("./pages/MagazineAdmin"));
const Shop = lazy(() => import("./pages/Shop"));
const ShopProduct = lazy(() => import("./pages/ShopProduct"));
const ShopCheckout = lazy(() => import("./pages/ShopCheckout"));
const RewardsAdmin = lazy(() => import("./pages/RewardsAdmin"));
const DocumentiCliente = lazy(() => import("./pages/DocumentiCliente"));
const SelfCheckout = lazy(() => import("./pages/SelfCheckout"));
const PolicyPage = lazy(() => import("./pages/PolicyPage"));
const PrivacyRights = lazy(() => import("./pages/PrivacyRights"));
const PrivacyRegister = lazy(() => import("./pages/PrivacyRegister"));
const PrivacyCenter = lazy(() => import("./pages/PrivacyCenter"));
const ProductPhotos = lazy(() => import("./pages/ProductPhotos"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const AmalfiClassic = lazy(() => import("./pages/AmalfiClassic"));
const LinoCollection = lazy(() => import("./pages/LinoCollection"));
const OurEyesPage = lazy(() => import("./pages/OurEyesPage"));
const BrandPreview = lazy(() => import("./pages/BrandPreview"));
const NotFound = lazy(() => import("./pages/NotFound"));
const LimonHelloCollection = lazy(() => import("./pages/LimonHelloCollection"));
import Home from "./pages/Home";
const Operativo = lazy(() => import("./pages/Operativo"));
const OpsBookings = lazy(() => import("./pages/OpsBookings"));
const DaFare = lazy(() => import("./pages/DaFare"));
const Notifiche = lazy(() => import("./pages/Notifiche"));
const ArchivioPrenotazioni = lazy(() => import("./pages/ArchivioPrenotazioni"));
const AnomalieIdentita = lazy(() => import("./pages/AnomalieIdentita"));
const MerchConcept = lazy(() => import("./pages/MerchConcept"));

const Loading = () => (window.__ppShellHTML && !window.__ppShellDone
  ? <div dangerouslySetInnerHTML={{ __html: window.__ppShellHTML }} />
  : <div className="min-h-screen grid place-items-center text-slate-400" data-testid="route-loading">…</div>);

function App() {
  return (
    <div className="App">
      <OfflineBanner />
      <Suspense fallback={null}><Toaster position="top-center" richColors /></Suspense>
      <AuthProvider>
        <BrowserRouter>
          <ConsentBanner />
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/vintage" element={<Vintage />} />
              <Route path="/magazine" element={<Magazine />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/shop/checkout" element={<ShopCheckout />} />
              <Route path="/shop/collezione/limonhello" element={<LimonHelloCollection />} />
              <Route path="/shop/categoria/:cat" element={<ShopCategory />} />
              <Route path="/shop/:slug" element={<ShopProduct />} />
              <Route path="/pagamento/esito" element={<PaymentResult />} />
              <Route path="/gestisci/:token" element={<ManageBooking />} />
              <Route path="/:lang/magazine" element={<Magazine />} />
              <Route path="/:lang/magazine/:dest" element={<Magazine />} />
              <Route path="/:lang/magazine/:dest/:slug" element={<Magazine />} />
              <Route path="/login" element={<Login />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/firma/:token" element={<Firma />} />
              <Route path="/documenti/:token" element={<DocumentiCliente />} />
              <Route path="/riconsegna/:token" element={<SelfCheckout />} />
              <Route path="/guida" element={<PublicGuide />} />
              <Route path="/privacy-policy" element={<PolicyPage kind="privacy" />} />
              <Route path="/cookie-policy" element={<PolicyPage kind="cookie" />} />
              <Route path="/legale/privacy-shop" element={<PolicyPage kind="shop" />} />
              <Route path="/legale/informativa-patenti" element={<PolicyPage kind="license" />} />
              <Route path="/legale/informativa-staff-partner" element={<PolicyPage kind="staff" />} />
              <Route path="/legale/diritti-privacy" element={<PrivacyRights />} />
              <Route path="/legale/:page" element={<LegalPage />} />
              <Route path="/condizioni-di-vendita" element={<LegalPage />} />
              <Route path="/terms-and-conditions" element={<LegalPage />} />
              <Route path="/amalfi-through-our-eyes" element={<OurEyesPage />} />
              <Route path="/collezione/lino-2027" element={<LinoCollection />} />
              <Route path="/collezione/lino-2027/:slug" element={<LinoCollection />} />
              <Route path="/collezione/amalfi-classic" element={<AmalfiClassic />} />
              <Route path="/collezione/amalfi-classic/:slug" element={<AmalfiClassic />} />
              <Route path="/brand-av" element={<BrandPreview />} />
              <Route path="/guida/:slug" element={<PublicGuide />} />
              <Route path="/q/free/:freeSlug" element={<PublicPartnerPage />} />
              <Route path="/q/:code" element={<QrRedirect />} />
              <Route path="/hotel/:slug" element={<PublicPartnerPage />} />
              <Route path="/hotel/:slug/s/:service" element={<PublicPartnerPage />} />
              <Route path="/destinazioni/:place" element={<PublicPartnerPage />} />
              <Route path="/destinazioni/:place/s/:service" element={<PublicPartnerPage />} />
              <Route path="/sito/:slug" element={<PublicPartnerPage />} />
              <Route path="/sito/:slug/s/:service" element={<PublicPartnerPage />} />
              <Route path="/stazione/:slug" element={<PublicPartnerPage />} />
              <Route path="/stazione/:slug/s/:service" element={<PublicPartnerPage />} />
              <Route path="/portale" element={<ProtectedRoute partner><Portale /></ProtectedRoute>} />
              <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
                <Route path="/operativo" element={<Operativo />} />
                <Route path="/da-fare" element={<DaFare />} />
                <Route path="/notifiche" element={<Notifiche />} />
                <Route path="/archivio-prenotazioni" element={<ArchivioPrenotazioni />} />
                <Route path="/calendario" element={<Navigate to="/operativo?tab=calendario" replace />} />
                <Route path="/gestione/:cat" element={<OpsBookings />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/dashboard/:fleet" element={<Dashboard />} />
                <Route path="/flotta" element={<Flotta />} />
                <Route path="/flotta/:id" element={<VeicoloDettaglio />} />
                <Route path="/prenotazioni" element={<Prenotazioni />} />
                <Route path="/contratti" element={<Archivio />} />
                <Route path="/contratti/nuovo" element={<NuovoContratto />} />
                <Route path="/contratti/:id" element={<ContrattoDettaglio />} />
                <Route path="/contratti/:id/modifica" element={<NuovoContratto />} />
                <Route path="/multe" element={<Multe />} />
                <Route path="/multe/cerca" element={<CercaMulta />} />
                <Route path="/tour" element={<Tour />} />
                <Route path="/tour/:id" element={<TourDettaglio />} />
                <Route path="/barca" element={<Barca />} />
                <Route path="/multe/:id" element={<MultaDettaglio />} />
                <Route path="/clienti" element={<Clienti />} />
                <Route path="/clienti/:id" element={<ClienteDettaglio />} />
                <Route path="/consegne" element={<Consegne />} />
                <Route path="/report" element={<Report />} />
                <Route path="/impostazioni" element={<Impostazioni />} />
                <Route path="/partner" element={<Partners />} />
                <Route path="/traghetti" element={<Partners stations />} />
                <Route path="/qr-free" element={<QrFree />} />
                <Route path="/sito-foto" element={<SiteMedia />} />
                <Route path="/partner/:id" element={<PartnerDetail />} />
                <Route path="/prenotazioni-partner" element={<PartnerBookings />} />
                <Route path="/guida-admin" element={<GuideAdmin />} />
                <Route path="/magazine-admin" element={<MagazineAdmin />} />
                <Route path="/rewards-admin" element={<RewardsAdmin />} />
                <Route path="/anomalie-identita" element={<AnomalieIdentita />} />
                <Route path="/privacy-richieste" element={<PrivacyRegister />} />
                <Route path="/centro-privacy" element={<PrivacyCenter />} />
                <Route path="/foto-prodotti" element={<ProductPhotos />} />
                <Route path="/merch-concept" element={<MerchConcept />} />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
            <HomeButton />
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
